import useIsCompactViewport from '@Guidelines/Component/FilterPanel/Hooks/useIsCompactViewport';
import { theme } from '@Guidelines/Theme/theme';
import useDocumentViewer from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/useDocumentViewer';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { css } from '@emotion/react';
import { Minus, MoveHorizontal, Plus } from 'lucide-react';
import * as React from 'react';
import { Button } from '@verclam/ui-kit';

const ZOOM_STEP = 0.1;

const groupStyle = css`
	margin: 0;
	border: 0;
	min-width: 0;
	display: flex;
	align-items: center;
	gap: 0.25rem;
	padding: 0.1875rem;
	border-radius: ${theme.radii.pill};
	background-color: ${theme.colors.surfaceMuted};
`;

const percentageStyle = css`
	min-width: 3rem;
	text-align: center;
	font-family: ${theme.fonts.mono};
	font-size: ${theme.fontSizes.small};
	font-weight: ${theme.fontWeights.medium};
	color: ${theme.colors.ink};
`;

const compactPercentageStyle = css`
	min-width: 2.5rem;
`;

/**
 * Zoom: − / percentage / + in steps of 10 %, and « Ajuster à la largeur ». On a phone the fit button
 * is icon-only (accessible name kept by `aria-label`; no `label`, which the kit would use instead).
 */
const ZoomControls: React.FC = () => {
	const isCompact = useIsCompactViewport();
	const { zoom, minZoom, maxZoom, setZoom, fitToWidth } = useDocumentViewer();

	const handleZoomOut = React.useCallback(() => {
		setZoom(zoom - ZOOM_STEP);
	}, [zoom, setZoom]);

	const handleZoomIn = React.useCallback(() => {
		setZoom(zoom + ZOOM_STEP);
	}, [zoom, setZoom]);

	return (
		<fieldset aria-label="Zoom" css={groupStyle}>
			<Button
				type="button"
				text
				rounded
				severity="secondary"
				aria-label="Réduire"
				icon={<Minus size={16} aria-hidden="true" />}
				disabled={zoom <= minZoom}
				onClick={handleZoomOut}
			/>
			<span
				css={[percentageStyle, isCompact ? compactPercentageStyle : null]}
			>{`${Math.round(zoom * 100)} %`}</span>
			<Button
				type="button"
				text
				rounded
				severity="secondary"
				aria-label="Agrandir"
				icon={<Plus size={16} aria-hidden="true" />}
				disabled={zoom >= maxZoom}
				onClick={handleZoomIn}
			/>
			<ConditionalLoader data={isCompact}>
				<Button
					type="button"
					text
					rounded
					severity="secondary"
					aria-label="Ajuster à la largeur"
					icon={<MoveHorizontal size={16} aria-hidden="true" />}
					onClick={fitToWidth}
				/>
			</ConditionalLoader>
			<ConditionalLoader data={!isCompact}>
				<Button
					type="button"
					text
					severity="secondary"
					label="Ajuster à la largeur"
					onClick={fitToWidth}
				/>
			</ConditionalLoader>
		</fieldset>
	);
};

export default ZoomControls;
